import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Row, Col, DatePicker, Button, Typography, Form, Table, Avatar, Tag, Tabs, Space, Card } from "antd";
import { PhoneOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment-timezone";
import metaData from "../../../data/metaData";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import csvUtil from "../../../utils/csv";
import dayjs from "../../../utils/day";
import tableUtil from "../../../utils/table";

//
import useAuthStore from "../../../states/useAuth";

//
import DelaySearch from "../../../components/Table/DelaySearch";
import ActionHeader from "../../../components/Table/ActionHeader";
import MainTabs from "../../../components/MerchantUser/Report/MainTabs";
import SectionHeader from "../../../components/GetStarted/SectionHeader";
import useReport from "../../../hooks/useQuery/useReport";
import TableColumns from "../../../components/MerchantUser/Report/TableColumns";
import { formatTime } from "../../../utils/time";
import { getSellingPrice } from "../../../utils/product";
import FilterDropdown from "../../../components/Table/FIlterDropdown";
import PeriodPicker from "../../../components/General/PeriodPicker";

const { RangePicker } = DatePicker;
const { Title, Text, Paragraph } = Typography;

const csvFileName = "distribute_record";

const Distribute = () => {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  //
  const { user, auth } = useAuthStore();
  const shopIds = user?.permission?.report;

  // Custom Date filter
  const MAX_RANGE_IN_DAYS = 90;
  const [timeRange, setTimeRange] = useState({ start: dayjs().subtract(MAX_RANGE_IN_DAYS, "day"), end: dayjs() });
  const [filterForm] = Form.useForm();

  //
  const logsQueryKey = useMemo(() => ["/report/distribute", shopIds, timeRange], [shopIds, timeRange]);
  const requestData = useMemo(() => {
    const data = {};

    // Add time filter only if timeRange exists
    if (timeRange?.start && timeRange?.end) {
      data.time = {
        $gte: timeRange.start,
        $lte: timeRange.end,
      };
    }
    // Conditional payload construction
    if (shopIds[0] === "all") {
      data.companyId = user.companyId;
    } else {
      data["shop.id"] = { $in: shopIds };
    }
    return data;
  }, [shopIds, user.companyId, timeRange]); // Include user.companyId if needed

  const {
    data: logs = [],
    isFetching: isFetchingLogs,
    error: logsError,
    refetch,
  } = useReport(
    !!shopIds, // Enable fetching only if shopIds exist
    logsQueryKey, // Unique query key
    "reports/distribute/query", // URL for the API request
    requestData // Request payload
  );

  //Table
  const [collapsed, setCollapsed] = useState(false);
  const [searchValue, setSearchValue] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(1);
  const [filteredDataCount, setFilteredDataCount] = useState(logs.length);
  const [filteredCSVData, setFilteredCSVData] = useState();
  const pageRef = useRef();

  const filteredData = useMemo(() => {
    // Start with all logs
    let result = [...logs];

    // Apply search filter if search value exists
    if (searchValue) {
      const lowerSearchValue = searchValue.toLowerCase();
      result = result.filter((record) => record?.id?.toLowerCase().includes(lowerSearchValue));
    }

    return result;
  }, [logs, searchValue]); // Only recalculate when these dependencies change

  const handleTimeRangeChange = ({ start, end }) => {
    setTimeRange({ start, end });
  };

  const resetDateFilter = () => {
    setTimeRange();
  };

  // Handle pagination change (page number or page size change)
  const handleTableChange = (pagination, filters, sorter, extra) => {
    const { current, pageSize } = pagination;
    console.log(current);
    setPagination(current);
    scrollToTop();

    // Store the filtered dataset for csv export
    setFilteredCSVData(extra.currentDataSource);

    setFilteredDataCount(extra.currentDataSource.length);
  };

  const scrollToTop = () => {
    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const shopFilterOptions = useMemo(() => {
    const key = "shop.name"; // For the shop filter, specify the key for the value to filter on
    return tableUtil.getDynamicFilterOptions(logs, key).map((value) => ({
      text: value,
      value,
    }));
  }, [logs]); // The filter options depend on `logs` data

  const translations = {
    recordIdTranslation: t("DCBKrecord.recordId"),
    timeTranslation: t("DCbuy.Time"),
    statusTranslation: t("DCbuy.status"),
    assistantTranslation: t("DCbuy.assistant"),
    consumerTranslation: t("DCbuy.consumer"),
    totalAmountTranslation: t("DCbuy.totalAmount"),
  };

  const filterOptions = {
    shopFilterOptions,
  };

  const { columns } = TableColumns({
    columnOrder: ["id", "time", "payment", "shop", "assistant", "client", "txTotalAmount", "txTotalQty"],
    translations,
    filterOptions,
  });

  const expandedRowRender = (record) => {
    // Define the order of columns you want for the expanded table
    const { columns: itemColumns } = TableColumns({
      columnOrder: ["product", "productType", "value", "price", "qty", "totalAmount"],
      translations,
    });

    return (
      <Table
        style={{
          tableLayout: "fixed",
          width: "60%",
        }}
        columns={itemColumns}
        dataSource={record.items}
        pagination={false}
        rowKey="id"
      />
    );
  };

  const getCSVRowData = (record) => {
    const rows = [];
    for (let i = 0; i < record.items.length; i++) {
      const item = record.items[i];

      // Get payment method label
      let paymentMethod;
      switch (record.payment.method) {
        case "online":
          paymentMethod =
            record.payment.gateway === "asiapay" ? "AsiaPay" : record.payment.gateway === "paypal" ? "PayPal" : "--";
          break;
        case "physical":
          paymentMethod = record.isFree ? "Bonus" : "Paid at Shop";
          break;
        case "promotionCode":
          paymentMethod = `Code: ${record?.payment?.code}`;
          break;
        case "estamp":
          paymentMethod = "Coin Redeem";
          break;
        case "welcome":
          paymentMethod = "Welcome";
          break;
        case "referrer":
          paymentMethod = "Referral";
          break;
        case "referee":
          paymentMethod = "Referral";
          break;
        default:
          paymentMethod = "--";
      }

      let price = getSellingPrice(item);
      const isFree =
        item?.isFree || ["welcome", "estamp", "promotionCode", "referrer", "referee"].includes(record.payment.method);
      const sellingPrice = isFree ? "Free" : price;
      const totalAmount = isFree ? "Free" : price * item.qty;
      const freeReasonMap = {
        qty: `Buy ${item?.discountMethod?.trigger?.value} Get ${item?.discountMethod?.reward?.value} Free`,
        referrer: "Referrer",
        referee: "Referee",
      };

      const freeReason = isFree
        ? freeReasonMap[item?.discountMethod?.trigger?.type] || freeReasonMap[record.payment.method] || paymentMethod
        : "--";

      const cashPaid = Math.max(0, record?.amount - record?.giftCardPaid);

      rows.push({
        [t("DCbuy.Time")]: formatTime(record.time, "YYYY-MM-DD HH:mm:ss"),
        [t("DCBKrecord.recordId")]: record.id || "--",
        ["Product Name"]: item.name || "--",
        ["Product ID"]: item.id || "--",
        ["GiftCard ID"]: item?.giftCard ? item.giftCard.id : "--",
        ["Product Type"]: item?.giftCard ? "Gift Card" : "Voucher",
        [t("DCbuy.qty")]: item.qty || "--",
        ["Value"]: item.price,
        ["Selling Price (CAD)"]: sellingPrice,
        ["Free Reason"]: freeReason,
        ["Total Amount (CAD)"]: totalAmount,
        ["Order Total Amount (CAD)"]: record.amount || "--",
        ["Paid by Cash (CAD)"]: cashPaid || "--",
        ["Paid by Gift Card (CAD)"]: record?.giftCardPaid || "--",
        Method: paymentMethod,
        [t("DCbuy.reference")]: record.payment.ref || "--",
        ["Shop Name"]: record.shop?.name || "--",
        [t("DCbuy.staffId")]: record.assistant?.email || "--",
        [t("DCredeem.consumerEmail")]: record.client?.email || "--",
        [t("DCredeem.consumerName")]: `${record.client?.nickname || ""} ${record.client?.name || ""}`.trim() || "--",
        [t("DCredeem.consumerTel")]: record.client?.tel || "--",
      });
    }
    return rows;
  };

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <SectionHeader
            title={
              <Title level={3} style={{ margin: 0 }}>
                Report
              </Title>
            }
          />
        </Col>
        <Col>
          <Space>
            <Button
              size="large"
              type="primary"
              onClick={() => {
                csvUtil.exportToCSV({
                  fileName: csvFileName,
                  csvData: (filteredCSVData === undefined || filteredCSVData.length === 0
                    ? filteredData
                    : filteredCSVData
                  ).flatMap(getCSVRowData),
                });
              }}
            >
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>

      <MainTabs />
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <ActionHeader
            pagination={pagination}
            pageSize={20}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            count={filteredDataCount}
            onActionClick={(value) => {
              log.debug(value);
            }}
          />

          {/* Filter Group */}
          <Card bordered style={{ marginBottom: "20px", background: "var(--ion-color-step-50)" }}>
            {/* <Row>
      <FilterDropdown
        label="Product Type"
        defaultValue="all"
        options={[
          { value: "all", label: "All" },
          { value: "voucher", label: "Voucher" },
          { value: "giftCard", label: "Gift Card" },
        ]}
      />
    </Row> */}
            <Row>
              <PeriodPicker
                state={{
                  data: timeRange,
                  form: filterForm,
                  onChange: handleTimeRangeChange,
                  keyName: "time",
                }}
                allowClear={false}
                maxRangeInDays={MAX_RANGE_IN_DAYS}
                style={{ width: "20rem" }}
              />
              <Button size="large" onClick={resetDateFilter} style={{ marginLeft: "1rem" }}>
                Clear Date
              </Button>
            </Row>
          </Card>

          <Table
            className="white-table-header"
            rowSelection={undefined}
            sticky
            loading={isFetchingLogs}
            columns={columns}
            dataSource={filteredData}
            pagination={{
              current: pagination,
              pageSize: 20,
              position: ["none", "bottomCenter"],
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
            scroll={{ x: "100%" }}
            expandable={{ expandedRowRender, expandRowByClick: true }}
          />

          <Row style={{ height: "5rem" }}></Row>
        </Col>
      </Row>
    </div>
  );
};

export default Distribute;
